import type { MelodyEntityNote, MelodyGrid, MelodyMeter } from '@tunasong/schemas'
import { beatMs, gridSlotBeats, gridSlotMs, GRID_TOLERANCE } from './grid.js'

/**
 * Capture quantizer: a performance in milliseconds becomes notes on a grid, and the distance each
 * note actually sat from its grid line is KEPT, as `offsetMs`. That is the whole point - the melody
 * entity's feel is the residue this function refuses to throw away (spec §2).
 *
 * Pure: milliseconds in, beats out. No audio, no clock, no React. Everything the capture flow gets
 * wrong upstream shows up here as a number a test can plant and read back.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5, §8
 */

/** One note as performed, in wall-clock milliseconds from the first beat of the take. */
export interface RawNote {
  midi: number
  /** ms from the take's beat 1. Negative means the player came in before the downbeat. */
  onsetMs: number
  durationMs: number
  /** 0-1, from MIDI attack or from the segment's peak level. */
  velocity: number
}

/**
 * Why a raw note did not become a melody note. Every dropped note is returned rather than silently
 * discarded: the review panel offers each one back a slot later, and a capture that quietly loses a
 * third of what was played is worse than one that says so.
 */
export type DropReason = 'too-short' | 'slot-taken' | 'before-take'

export interface DroppedRawNote extends RawNote {
  reason: DropReason
  /** Index into the `raw` array passed in, so the UI can shift exactly this note and re-quantize. */
  sourceIndex: number
}

export interface QuantizeOptions {
  bpm: number
  /**
   * Accepted because §5 names it and every caller has it to hand - NOT read. Grid slots are
   * quarter-note fractions, so the snapping arithmetic is meter-independent by construction
   * (@see ./grid.js): switching 4/4 to 6/8 moves the bar lines and never the notes. It stays in the
   * signature so the one place a meter-dependent rule could ever land already has it.
   */
  meter: MelodyMeter
  grid: MelodyGrid
  /** The device's measured input latency, subtracted from every onset before snapping. */
  inputLatencyMs?: number
}

export interface QuantizeResult {
  notes: MelodyEntityNote[]
  dropped: DroppedRawNote[]
}

/**
 * The shortest thing that is a note rather than a glitch: a 64th, which is a sixteenth of a
 * quarter-note beat - 31 ms at 120 bpm.
 *
 * Below this nothing a player produced survives as a pitch anyone hears: it is under the ~50 ms a
 * listener needs to assign a pitch to a tone at all, and it is under one analysis hop plus the
 * 100 ms scoop the segmenter already discards, so an audio segment this short cannot have been
 * measured in the first place. On the MIDI side it is a key brushed on the way past. §5 names the
 * 64th; this is that value in beats.
 */
export const MIN_RAW_DURATION_BEATS = 1 / 16

/** Highest velocity wins the slot; ties go to the note that arrived first, so the rule is stable. */
const louder = (a: RawNote, b: RawNote): boolean => a.velocity > b.velocity

export const quantizeRawNotes = (raw: RawNote[], options: QuantizeOptions): QuantizeResult => {
  const { bpm, grid, inputLatencyMs = 0 } = options

  const slotMs = gridSlotMs(grid, bpm)
  const slotBeats = gridSlotBeats(grid)
  const minDurationMs = MIN_RAW_DURATION_BEATS * beatMs(bpm)

  const dropped: DroppedRawNote[] = []
  /** Winner per slot index, so the collision rule is decided before any note is emitted. */
  const bySlot = new Map<number, { note: RawNote; sourceIndex: number; offsetMs: number }>()

  raw.forEach((note, sourceIndex) => {
    const drop = (reason: DropReason) => dropped.push({ ...note, reason, sourceIndex })

    if (note.durationMs < minDurationMs - GRID_TOLERANCE) {
      drop('too-short')
      return
    }

    /** Latency first: everything after this reads a corrected clock, the bounds check included. */
    const onsetMs = note.onsetMs - inputLatencyMs
    const slotIndex = Math.round(onsetMs / slotMs)
    if (slotIndex < 0) {
      /*
       * Clamping to slot 0 would write an offset larger than half a slot, which `validateMelody`
       * rejects, and it would be a lie about when the note was played. A pickup that early is
       * offered back in the review panel instead.
       */
      drop('before-take')
      return
    }

    const offsetMs = onsetMs - slotIndex * slotMs
    const incumbent = bySlot.get(slotIndex)
    if (!incumbent) {
      bySlot.set(slotIndex, { note, sourceIndex, offsetMs })
      return
    }
    if (louder(note, incumbent.note)) {
      dropped.push({ ...incumbent.note, reason: 'slot-taken', sourceIndex: incumbent.sourceIndex })
      bySlot.set(slotIndex, { note, sourceIndex, offsetMs })
      return
    }
    drop('slot-taken')
  })

  const notes = [...bySlot.entries()]
    .sort(([a], [b]) => a - b)
    .map(([slotIndex, { note, offsetMs }]) => ({
      midi: note.midi,
      onset: slotIndex * slotBeats,
      /** At least one slot: a note shorter than the grid still has to occupy a place on it. */
      duration: Math.max(1, Math.round(note.durationMs / slotMs)) * slotBeats,
      velocity: note.velocity,
      offsetMs,
    }))

  /*
   * A melody is single-voice (§2), so a note running into the next one is shortened to end where
   * the next begins. It can never be shortened below one slot: two notes that snapped to the same
   * slot cannot both be here - one of them was dropped above - so consecutive onsets differ by at
   * least one slot.
   */
  for (let i = 0; i < notes.length - 1; i++) {
    const gap = notes[i + 1].onset - notes[i].onset
    if (notes[i].duration > gap + GRID_TOLERANCE) {
      notes[i] = { ...notes[i], duration: gap }
    }
  }

  return { notes, dropped }
}

/**
 * Move one raw note in time and return a new list. This is how a dropped note is restored: the
 * review panel shifts it by one slot and re-quantizes the whole take, so the restored note is
 * subject to exactly the same rules as everything else - including losing its new slot to a louder
 * neighbour, which the user then sees.
 */
export const shiftRawNote = (raw: RawNote[], index: number, deltaMs: number): RawNote[] =>
  raw.map((note, i) => (i === index ? { ...note, onsetMs: note.onsetMs + deltaMs } : note))
